import { useCallback, useEffect, useState } from "react";
import { GetJournalCountsUseCase, JournalCountsEntity } from "../../domain";
import { JournalRepositoryImpl } from "../../data/repositories/JournalRepositoryImpl";

const journalRepository = new JournalRepositoryImpl();
const getJournalCountsUseCase = new GetJournalCountsUseCase(journalRepository);

export interface UseJournalCountsReturn {
  counts: JournalCountsEntity | null;
  loading: boolean;
  error: string | null;
  fetchCounts: (params?: {
    date_from?: string;
    date_to?: string;
  }) => Promise<void>;
  clearError: () => void;
}

export const useJournalCounts = (initialParams?: {
  date_from?: string;
  date_to?: string;
}): UseJournalCountsReturn => {
  const [counts, setCounts] = useState<JournalCountsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCounts = useCallback(async (params?: {
    date_from?: string;
    date_to?: string;
  }) => {
    setLoading(true);
    setError(null);

    try {
      const result = await getJournalCountsUseCase.execute(params);
      setCounts(result);
    } catch (error: any) {
      setError(error.message || "Failed to load journal counts");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    fetchCounts(initialParams);
  }, [fetchCounts, initialParams?.date_from, initialParams?.date_to]);

  return {
    counts,
    loading,
    error,
    fetchCounts,
    clearError,
  };
};
